import React, { useEffect, useState } from 'react';
import { ListGroup, Badge,Card, Form, Button, Alert, FormCheck, FormControl } from "react-bootstrap";
import { MessageTemplate, MessageTranslation, StoredMessageTemplate } from '../../api/types/messages';
import { useAvailableLanguages } from '../../hooks/useAvailableLanguages';
import clsx from 'clsx';
import { MessageTranslationEditor } from './MessageTranslationEditor';
import { Icon } from '../ui/Icon';
import { useDefaultLanguage } from '../../hooks/useDefaultLanguage';
import { Language } from '../../api/types/config';
import { HelpTip } from '../ui/overlays';
import { ConfirmButton } from '../utils/ConfirmButton';
import { useAvailableStudies } from '../../hooks/useAvailableStudies';

interface TranslationAddSelectorProps {
    translations: Array<MessageTranslation>;
    availableLanguages: Array<Language>;
    onAdd:(lang:string)=>void;
}
 
const TranslationAddSelector: React.FC<TranslationAddSelectorProps> = (props) => {

   const codes = new Set<string>(props.translations.map((t)=> t.lang));
   const languages =  props.availableLanguages.filter( (l)=> !codes.has(l.code));
   
   const show = languages.length > 0;

   return <React.Fragment>
       <div className={ clsx({"invisible": !show}, 'm-1') }>
       Add <Icon name="language"/> : { languages.map((lang) => <Button key={lang.code} onClick={()=>props.onAdd(lang.code)} size="sm" className='me-1'>{lang.label}</Button>) }
       </div>
   </React.Fragment>
 }
 
 interface MessagesTranslationItemProps {
    trans: MessageTranslation;
    onEdit: (lang:string)=>void;
    onDelete: (lang:string)=>void;
 }
 
 const MessagesTranslationItem: React.FC<MessagesTranslationItemProps> = (props) => {
    return <ListGroup.Item key={props.trans.lang}>
       <Badge bg="secondary" className='me-1'>{props.trans.lang}</Badge>
       <span className='text-italic me-1'>{props.trans.subject}</span>
       <Button onClick={(ev) => props.onEdit(props.trans.lang)} size="sm" className='me-1'>Edit <Icon name="edit"/></Button>
       <ConfirmButton text="Are you sure to remove this translation" variant="danger" size="sm" onConfirm={()=> props.onDelete(props.trans.lang)}><Icon name="trash"/> Delete</ConfirmButton>
    </ListGroup.Item>
 };

 interface MessagesTranslationListProps {
    translations: MessageTranslation[];
    availableLanguages: Array<Language>
    onEdit: (lang:string)=>void;
    onAdd: (lang:string)=>void;
    onDelete: (lang:string)=>void;
 }
 
 const MessagesTranslationList: React.FC<MessagesTranslationListProps> = (props) => {
    return <React.Fragment>
        <ListGroup>
        { props.translations.map( (trans)=> <MessagesTranslationItem trans={trans} onEdit={props.onEdit} onDelete={props.onDelete} key={trans.lang}/> ) }
        </ListGroup> 
        <TranslationAddSelector onAdd={props.onAdd} translations={props.translations} availableLanguages={props.availableLanguages}/>
    </React.Fragment>
 }

export type EditableMessage = MessageTemplate|StoredMessageTemplate;

 interface MessageEditorProps {
    template?: EditableMessage;
    mode: 'template' | 'stored'
    factory: ()=>EditableMessage
    allowedMessageTypes: string[]
    onSave: (m:EditableMessage)=>void;
    onCancel: ()=>void;
 }

 export const MessagesEditor: React.FC<MessageEditorProps> = (props: MessageEditorProps) => {
 
   const platformDefaultLanguage = useDefaultLanguage();

   const availableLanguages = useAvailableLanguages();

   const availableStudies = useAvailableStudies();

   const [error, setError] = useState<string>('');
   const [editedTrans, setEditedTrans] = useState<MessageTranslation|undefined>(undefined);
   const [defaultLanguage, setDefaultLanguage] = useState<string>( props.template?.defaultLanguage ?? platformDefaultLanguage);
   const [messageType, setMessageType] = useState<string>(props.template?.messageType ?? '');
   const [studyKey, setStudyKey] = useState<string>(props.template?.studyKey ?? '');
   const [save, setSave] = useState<boolean>(false);
   const [internalName, setInternalName] = useState<string>(()=>{
      if(!props.template) {
         return '';
      }
      if ('internal_name' in props.template) {
         return props.template.internal_name ?? '';
      }
      return '';
   });
   
   useEffect(()=>{
      if(!save) {
         return;
      }
      const m = props.factory();
      m.id = props.template ? props.template.id : undefined;
      m.studyKey=  studyKey;
      m.translations= translations;
      m.defaultLanguage= defaultLanguage;
      m.messageType= messageType;

      if(messageType === '') {
         setError("Message type cannot be empty, select one");
         return;
      }

      if(props.mode === 'stored') {
         (m as StoredMessageTemplate).internal_name = internalName;
      }
      props.onSave(m);
      setSave(false);
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [save]);

   const trans = props.template?.translations ?? [];
 
   const [translations, setTranslations] = useState( trans );

   const handleAddTrans = (lang:string)=>{
       const t : MessageTranslation = {
          lang: lang,
          subject: '',
          templateDef: ''
       };
       const tt = [t, ...translations];
       setTranslations(tt);
   };
 
   const handleEditTrans = (lang:string)=> {
        const trans = translations.find((t)=>t.lang === lang);
        if(!trans) {
            setError("Unable to find trans for lang:"+lang);
            return;
        }
        setEditedTrans(trans);
   };
 
   const handleSave=()=>{
      setSave(true);
   };
 
   const handleCancel=()=>{
    if(props.onCancel) {
       props.onCancel();
    }
   }

   const handleTransCancel=() => {
        setEditedTrans(undefined);
   };

   const handleSaveTrans=(trans: MessageTranslation) => {
     const tt = translations.filter((t)=>t.lang !== trans.lang);
     tt.push(trans);
     setTranslations(tt);
     setEditedTrans(undefined);
     setSave(true);
   };

   const handleDeleteTrans=(lang:string) => {
      const tt = translations.filter((t)=>t.lang !== lang);
      setTranslations(tt);
      setSave(true);
    };

   const id = props.template?.id ?? 0;

   const isEditingTrans = typeof(editedTrans) === "object";

   return (
   <Card className='p-1'>
    <Card.Title>
    <h5>{ id ? 'Message #' + id : 'New template message'}</h5>
    </Card.Title>
    <Card.Body>
       <Form>
          { error ? <Alert variant='danger'>{error}</Alert> : ''}
          <Form.Group className='mt-1'>
             <Form.Label>Default Language <HelpTip content="Selected language when account doesnt has preferred language specified"/></Form.Label>
             <div className='mt-0 mb-1'>
             { availableLanguages.map((lang) => 
                <label key={lang.code} className='me-1'><input name="msg-editor-default-lng" type="radio" value={lang.code} onClick={() => setDefaultLanguage(lang.code)} defaultChecked={lang.code === defaultLanguage} /> {lang.label}</label>
               )
             }
             [Current stored = {defaultLanguage}]
             </div>
          </Form.Group>
          <Form.Group className='mt-1'>
             <Form.Label>StudyKey <HelpTip content="Key of the study, only used for message with condition"/></Form.Label>
             <Form.Select onChange={(e)=> setStudyKey(e.currentTarget.value) } defaultValue={studyKey}>
               <option value="">-None-</option>
               { availableStudies.map(study => <option key={study} value={study}>{study}</option>)}
             </Form.Select>
          </Form.Group>
          { props.mode === 'stored' ? 
          <Form.Group className='mt-1'>
             <Form.Label>Internal Name (Name only used in this admin website, not populated on the platform)</Form.Label>
             <FormControl type='text' onChange={e=>setInternalName(e.currentTarget.value)} defaultValue={internalName}></FormControl>
          </Form.Group> : ''
         }
          <Form.Group className='mt-1'>
             <Form.Label>MessageType <HelpTip content="Message type define the behavior on how message is handled on the platform : which variables can be used, when deliver it (use weekday for weekly)"/></Form.Label>
             { props.allowedMessageTypes.map(
               type => 
                  <FormCheck key={type} type="radio" value={type} label={type} onChange={e => setMessageType(e.currentTarget.value)} defaultChecked={type === messageType}/>
               )
             }
          </Form.Group>
          <Form.Group className='my-1 p-1 border rounded'>
            <Form.Label>Translations <HelpTip content="Translations defines the content of the message"/></Form.Label>
             { !editedTrans ? <MessagesTranslationList key="editor" translations={translations} onAdd={handleAddTrans} onEdit={handleEditTrans} onDelete={handleDeleteTrans} availableLanguages={availableLanguages}/> : '' }
             { editedTrans ? <MessageTranslationEditor trans={editedTrans} onCancel={handleTransCancel} onSubmit={handleSaveTrans}/> : ''}
          </Form.Group>
         
          { !isEditingTrans ? 
            <Form.Group>
               <Button variant='primary' onClick={handleSave} className="me-1">Save message</Button>
               <Button variant='danger' onClick={handleCancel}>Cancel</Button>
             </Form.Group>
            : ''
          } 
       </Form>
    </Card.Body>
    </Card>
   );
 }